import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Table, Pagination, PaginationItem, PaginationLink } from '../../../shim/reactstrap';
import apiClient from '../../../api/ApiClient';

/**
 * Admin dialog listing candle dates that exist in the live DB but are
 * absent from the uploaded original CTRAC package. Bounded by the
 * intersection of both date ranges — and capped at the original
 * package's last date so DB-only candles past the package horizon are
 * never surfaced.
 *
 * Each row carries a "Remove candles from DB" button that deletes every
 * candle row for that (tickerId, date, "Day") tuple and refreshes the
 * list. Matches the styling / pagination pattern of DataSourceDifferencesModal
 * so the two screens feel like siblings.
 */
export const ExtraDbDatesModal = ({
  isOpen,
  toggle,
  subfolder,
  tickerCode,
  tickerId,
  tickerName,
  onDatesChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingDate, setDeletingDate] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [confirmingDate, setConfirmingDate] = useState(null);
  const pageSize = 50;

  const fetchDates = useCallback(async (page) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.getCandleExtraDbDates(subfolder, tickerCode, tickerId, page, pageSize);
      setData(result);
      setCurrentPage(page);
    } catch (exc) {
      console.error('Failed to fetch extra DB dates:', exc);
      setError(exc.message || 'Failed to fetch extra DB dates');
    } finally {
      setLoading(false);
    }
  }, [subfolder, tickerCode, tickerId]);

  useEffect(() => {
    if (isOpen) {
      fetchDates(1);
      setDeleteError(null);
      setConfirmingDate(null);
    }
  }, [isOpen, fetchDates]);

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && data && page <= data.TotalPages) {
      fetchDates(page);
    }
  };

  // Two-step delete: first click stages the row (orange), second click confirms.
  // Matches the deliberate-action UX used by the rest of the admin screens.
  const beginDeleteFlow = (dateValue) => {
    setDeleteError(null);
    setConfirmingDate(dateValue);
  };

  const cancelDeleteFlow = () => {
    setConfirmingDate(null);
  };

  const performDelete = async (dateValue) => {
    try {
      setDeletingDate(dateValue);
      setDeleteError(null);
      const result = await apiClient.deleteCandleByDate(tickerId, dateValue, 'Day');
      setConfirmingDate(null);
      // Refresh the list from page 1 because deletion shifts the page contents.
      // We intentionally don't preserve the current page — the count change is
      // the more visible event and admins typically start from the top after
      // a batch cleanup.
      const wasLastPage = data && currentPage === data.TotalPages;
      if (wasLastPage && currentPage > 1) {
        await fetchDates(currentPage - 1);
      } else {
        await fetchDates(currentPage);
      }
      // Surface the deleted count via the optional callback so the parent
      // page can refresh its own extra-dates badge / count.
      if (typeof onDatesChanged === 'function') {
        try { onDatesChanged(result); } catch (cbErr) { console.warn('onDatesChanged callback failed:', cbErr); }
      }
    } catch (exc) {
      console.error('Failed to delete candle:', exc);
      setDeleteError(exc.message || 'Failed to delete candle from DB');
    } finally {
      setDeletingDate(null);
    }
  };

  const renderPagination = () => {
    if (!data || data.TotalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(data.TotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PaginationItem key="first">
          <PaginationLink first onClick={() => handlePageChange(1)} />
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
      </PaginationItem>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="next" disabled={currentPage === data.TotalPages}>
        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
      </PaginationItem>
    );

    if (endPage < data.TotalPages) {
      pages.push(
        <PaginationItem key="last">
          <PaginationLink last onClick={() => handlePageChange(data.TotalPages)} />
        </PaginationItem>
      );
    }

    return <Pagination>{pages}</Pagination>;
  };

  const title = tickerName || tickerCode || `Ticker #${tickerId}`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Extra DB Dates — {title}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <strong>Folder:</strong> <code>{subfolder}</code>{' '}
          <strong className="ms-3">Ticker:</strong> <code>{tickerCode}</code>
        </div>

        {data?.IntersectionStart && data?.UpperBound && (
          <div className="mb-3">
            <strong>Search range:</strong>{' '}
            {new Date(data.IntersectionStart).toLocaleDateString()} &ndash;{' '}
            {new Date(data.UpperBound).toLocaleDateString()}
            <span className="ms-2 text-muted" style={{ fontSize: '0.9em' }}>
              (DB candle dates within the original package&rsquo;s range that are missing from the original CSV)
            </span>
          </div>
        )}

        {error && (
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
        )}

        {deleteError && (
          <Alert color="warning" fade={false}>
            {deleteError}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading extra DB dates...</div>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="mb-3">
              <strong>Total extra DB dates:</strong> {data.TotalCount}
              {data.TotalCount > 0 && (
                <span className="ms-2 text-muted">
                  (Showing {((currentPage - 1) * pageSize) + 1} &ndash; {Math.min(currentPage * pageSize, data.TotalCount)})
                </span>
              )}
            </div>

            {data.Items && data.Items.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table striped bordered size="sm">
                    <thead>
                      <tr>
                        <th style={{ width: '160px' }}>Date</th>
                        <th style={{ width: '220px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.Items.map((rawDate, idx) => {
                        // Normalize to a JS Date — Item could be a string, a
                        // Date, or an offset-DateTime-shaped object depending
                        // on the JSON serializer. ToISOString() / Date() both
                        // work for ISO strings; for "{ iso }" objects we pull
                        // a usable value out.
                        const dateValue = normalizeItemDate(rawDate);
                        const dateLabel = dateValue ? dateValue.toLocaleDateString() : `row #${idx}`;
                        const isConfirming = confirmingDate && dateValue
                          ? confirmingDate === rawDate || confirmingDate.getTime?.() === dateValue.getTime()
                          : false;
                        const isDeleting = deletingDate && dateValue
                          ? deletingDate === rawDate || (deletingDate.getTime && dateValue && deletingDate.getTime() === dateValue.getTime())
                          : false;
                        return (
                          <tr key={`${dateLabel}-${idx}`}>
                            <td><strong>{dateLabel}</strong></td>
                            <td>
                              {!isConfirming ? (
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => beginDeleteFlow(dateValue)}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <>
                                      <Spinner size="sm" className="me-2" />
                                      Removing...
                                    </>
                                  ) : (
                                    'Remove candles from DB'
                                  )}
                                </Button>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => performDelete(rawDate)}
                                    disabled={isDeleting}
                                    className="me-2"
                                  >
                                    {isDeleting ? (
                                      <>
                                        <Spinner size="sm" className="me-2" />
                                        Removing...
                                      </>
                                    ) : (
                                      'Confirm remove'
                                    )}
                                  </Button>
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    outline
                                    onClick={cancelDeleteFlow}
                                    disabled={isDeleting}
                                  >
                                    Cancel
                                  </Button>
                                  <span className="ms-2 text-warning" style={{ fontSize: '0.85em' }}>
                                    Confirm to delete the candle row for this date.
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-center mt-3">
                  {renderPagination()}
                </div>
              </>
            ) : (
              <Alert color="success" fade={false}>
                No extra DB dates found in the searched range.
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Helpers ---------------------------------------------------------------

// The backend serializes DateTime values as ISO strings, but in some
// serializer paths the value may arrive as a { "ISO": ... } wrapper or
// another shape. Coerce it to a Date when possible, otherwise return null.
function normalizeItemDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Object shape — try common date-pickup keys.
  if (typeof value === 'object') {
    const candidate = value.date || value.Date || value.time || value.Time || value.iso || value.ISO;
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      const d = new Date(candidate);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}
